/**
 * MinimizeNote Component
 *
 * Description:
 * A draggable, floating card modal that contains a notes management system for POS orders.
 * Includes form inputs for title and content, next appointment date/time scheduling (restricting past dates),
 * and an interactive history records tab with inline editing and deletion capabilities.
 *
 * Usage:
 * <MinimizeNote
 *   visible={showMinimizeNote}
 *   onCancel={() => setShowMinimizeNote(false)}
 *   onSave={(noteData) => handleSaveNote(noteData)}
 * />
 *
 * Used in:
 * - src/views/pages/pos/components/PosOrderHeader.js (triggered by clicking "บันทึกโน็ต" button)
 */

import React from "react";
import {
  Button,
  Input,
  Checkbox,
  DatePicker,
  Typography,
  Tabs,
  Spin,
  Select,
} from "antd";
import {
  FaArrowsAlt,
  FaMinus,
  FaExpandAlt,
  FaStickyNote,
  FaTimes,
  FaUser,
  FaIdCard,
} from "react-icons/fa";
import {
  thLocale,
  contactResultOptions,
  reasonOptions,
  REASON_REQUIRED_RESULTS,
} from "./constants";
import { useMinimizeNote } from "./useMinimizeNote";
import HistoryTab from "./HistoryTab";
import { getUserObj } from "../../utils";
import "./minimize-note-styles.css";

const { Text } = Typography;

const MinimizeNote = ({
  visible,
  partnerCode,
  partnerName,
  partnerType,
  onCancel,
  onSave,
  onDirtyChange,
}) => {
  // Leverage custom business logic hook
  const {
    position,
    handleMouseDown,
    isDragging,
    isMinimized,
    handleMinimize,
    handleExpand,
    hasDraggedRef,
    title,
    setTitle,
    content,
    setContent,
    contactStatus,
    setContactStatus,
    reason,
    setReason,
    hasPurchase,
    setHasPurchase,
    hasNextAppt,
    setHasNextAppt,
    apptDate,
    setApptDate,
    apptNote,
    setApptNote,
    activeTab,
    setActiveTab,
    isLoading,
    notes,
    page,
    limit,
    total,
    loadNotes,
    handleSave,
    handleEditAppointment,
    disabledDate,
    isSaveDisabled,
  } = useMinimizeNote({
    visible,
    partnerCode,
    partnerType,
    onCancel,
    onSave,
    onDirtyChange,
  });

  if (!visible) return null;

  // Render floating minimized pill widget
  if (isMinimized) {
    return (
      <div
        className="minimize-note-pill"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          cursor: isDragging ? "grabbing" : "grab",
        }}
        onMouseDown={handleMouseDown}
        onClick={() => {
          if (!hasDraggedRef.current) {
            handleExpand();
          }
        }}
        title="คลิกเพื่อขยาย หรือลากเพื่อย้ายตำแหน่ง"
      >
        <div className="minimize-note-pill-icon">
          <FaStickyNote style={{ fontSize: "14px" }} />
        </div>
        <span className="minimize-note-pill-title">
          บันทึกการติดต่อ {partnerCode !== "-" ? `(${partnerCode})` : ""}
        </span>
        <div className="minimize-note-pill-actions">
          <span
            className="minimize-note-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleExpand();
            }}
            title="ขยาย"
          >
            <FaExpandAlt style={{ fontSize: "11px" }} />
          </span>
          <span
            className="minimize-note-action-btn minimize-note-close-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (onCancel) onCancel();
            }}
            title="ปิด"
          >
            <FaTimes style={{ fontSize: "12px" }} />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="minimize-note-modal"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {/* Draggable Header */}
      <div
        onMouseDown={handleMouseDown}
        className="minimize-note-header"
        style={{
          cursor: isDragging ? "grabbing" : "grab",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <FaArrowsAlt style={{ fontSize: "14px", marginRight: "8px" }} />
          <span>บันทึกการติดต่อ</span>
        </div>
        <div className="minimize-note-header-actions">
          <span
            className="minimize-note-action-btn"
            onClick={handleMinimize}
            title="ย่อขนาด"
          >
            <FaMinus style={{ fontSize: "12px" }} />
          </span>
          <span
            className="minimize-note-action-btn minimize-note-close-btn"
            onClick={onCancel}
            title="ปิด"
          >
            <FaTimes style={{ fontSize: "12px" }} />
          </span>
        </div>
      </div>

      {/* Member & Operator Info Banner */}
      <div className="minimize-note-info-banner">
        <div className="info-banner-item">
          <FaUser
            style={{ fontSize: "11px", color: "#1890ff", flexShrink: 0 }}
          />
          <span className="info-banner-label">ผู้ดำเนินการ:</span>
          <span className="info-banner-value">{partnerName}</span>
        </div>
        <div className="info-banner-divider" />
        <div className="info-banner-item">
          <FaIdCard
            style={{ fontSize: "11px", color: "#fa8c16", flexShrink: 0 }}
          />
          <span className="info-banner-label">รหัสสมาชิก:</span>
          <span className="info-banner-value">{partnerCode}</span>
        </div>
      </div>

      {/* Tabs and Form/History content */}
      <div className="minimize-note-body">
        <Spin spinning={isLoading}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)}
            size="small"
          >
            {/* TAB 1: Add note form */}
            <Tabs.TabPane tab="เพิ่มการบันทึก" key="1">
              <div style={{ marginTop: "14px" }}>
                {/* <div className="form-field-group">
                  <Text strong className="form-label">
                    หัวข้อ
                  </Text>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="กรอกหัวข้อ"
                    maxLength={100}
                    className="form-input-rounded"
                  />
                </div> */}

                <div className="form-field-group">
                  <Text strong className="form-label">
                    ผลการติดต่อ <span style={{ color: "#ef4444" }}>*</span>
                  </Text>
                  <Select
                    value={contactStatus}
                    onChange={(val) => {
                      setContactStatus(val);
                      if (!REASON_REQUIRED_RESULTS.has(val)) {
                        setReason(undefined);
                      }
                    }}
                    placeholder="— เลือกผลการติดต่อ —"
                    style={{ width: "100%", marginTop: "6px" }}
                    className="form-input-rounded"
                    dropdownStyle={{ maxHeight: 180, overflowY: "auto" }}
                    dropdownClassName="minimize-note-select-dropdown"
                    getPopupContainer={(trigger) => trigger.parentNode}
                  >
                    {contactResultOptions.map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {REASON_REQUIRED_RESULTS.has(contactStatus) && (
                  <div className="form-field-group">
                    <Text strong className="form-label">
                      สาเหตุ <span style={{ color: "#ef4444" }}>*</span>
                    </Text>
                    <Select
                      value={reason}
                      onChange={(val) => setReason(val)}
                      placeholder="— เลือกสาเหตุ —"
                      style={{ width: "100%", marginTop: "6px" }}
                      className="form-input-rounded"
                      dropdownStyle={{ maxHeight: 180, overflowY: "auto" }}
                      dropdownClassName="minimize-note-select-dropdown"
                      getPopupContainer={(trigger) => trigger.parentNode}
                    >
                      {reasonOptions.map((opt) => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                )}

                <div className="form-field-group">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Text
                      strong
                      className="form-label"
                      style={{ marginBottom: 0 }}
                    >
                      เนื้อหา
                    </Text>
                    <span style={{ fontSize: "11px", color: "#8c8c8c" }}>
                      ({content.length}/500)
                    </span>
                  </div>
                  <Input.TextArea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="กรอกเนื้อหา"
                    rows={3}
                    maxLength={500}
                    showCount
                    className="form-input-rounded"
                  />
                </div>

                <div
                  className="form-field-group"
                  style={{ display: "flex", gap: "20px", alignItems: "center" }}
                >
                  <Checkbox
                    checked={hasPurchase}
                    onChange={(e) => setHasPurchase(e.target.checked)}
                  >
                    <span className="checkbox-text">มีการซื้อ</span>
                  </Checkbox>

                  <Checkbox
                    checked={hasNextAppt}
                    onChange={(e) => setHasNextAppt(e.target.checked)}
                  >
                    <span className="checkbox-text">นัดหมายครั้งถัดไป</span>
                  </Checkbox>
                </div>

                {hasNextAppt && (
                  <div className="appointment-dashed-box">
                    <div style={{ marginBottom: "12px" }}>
                      <Text strong className="d-block form-label-small">
                        วันที่และเวลานัดหมายครั้งถัดไป
                      </Text>
                      <div className="datepicker-wrapper">
                        <DatePicker
                          showTime={{ format: "HH:mm" }}
                          format="YYYY-MM-DD HH:mm"
                          disabledDate={disabledDate}
                          value={apptDate}
                          onChange={(value) => setApptDate(value)}
                          placeholder="เลือกวันที่และเวลา"
                          style={{ width: "100%" }}
                          getCalendarContainer={(trigger) => trigger.parentNode}
                          locale={thLocale}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Text
                          strong
                          className="form-label-small"
                          style={{ marginBottom: 0 }}
                        >
                          รายละเอียดการนัดหมาย
                        </Text>
                        <span style={{ fontSize: "11px", color: "#8c8c8c" }}>
                          ({apptNote.length}/200)
                        </span>
                      </div>
                      <Input.TextArea
                        value={apptNote}
                        onChange={(e) => setApptNote(e.target.value)}
                        placeholder="รายละเอียดเรื่องที่นัดหมาย"
                        rows={2}
                        maxLength={200}
                        showCount
                        className="form-input-rounded"
                      />
                    </div>
                  </div>
                )}

                <div className="buttons-footer">
                  <Button onClick={onCancel} className="btn-rounded">
                    ยกเลิก
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                    className="btn-rounded"
                  >
                    บันทึก
                  </Button>
                </div>
              </div>
            </Tabs.TabPane>

            <Tabs.TabPane tab="ประวัติการบันทึก" key="2">
              <HistoryTab
                notes={notes}
                page={page}
                limit={limit}
                total={total}
                onChangePage={(p) => loadNotes(p)}
                onEditAppointment={handleEditAppointment}
              />
            </Tabs.TabPane>
          </Tabs>
        </Spin>
      </div>
    </div>
  );
};

export default MinimizeNote;
